/* Where the API and the sign-in service are.
 *
 * THREE CASES, IN ORDER OF PRECEDENCE.
 *
 * 1. An explicit build-time URL. Production sets both, because there the API
 *    is a real host with a real certificate and nothing should be inferred.
 *
 * 2. Served over https, which locally means behind the demo proxy: everything
 *    is SAME-ORIGIN under a path. One origin, one certificate, no CORS and no
 *    preflight. That is not a preference, it is the fix: talking to three
 *    origins meant three certificates to be trusted and a CORS negotiation on
 *    every call, and both of those failed on Safari while passing in Chrome.
 *
 * 3. Plain http, which means Vite is being hit directly during development.
 *    The services are on their own ports and CORS is exercised, which is worth
 *    keeping so the production path is not only tested by its unit tests.
 *
 * Derived from the page's own address rather than hard-coded to localhost,
 * because a phone on the office wifi cannot reach the laptop's localhost.
 */
const loc = typeof window === "undefined" ? null : window.location;
const host = loc?.hostname ?? "localhost";
const origin = loc?.origin ?? "http://localhost";
const secure = loc?.protocol === "https:";

export const API_URL =
  import.meta.env.VITE_API_URL ??
  (secure ? `${origin}/api/v1` : `http://${host}:8000/v1`);

export const AUTH_URL =
  import.meta.env.VITE_AUTH_URL ??
  (secure ? `${origin}/auth` : `http://${host}:9999`);

/* Whether the till offers the phone-and-OTP route.
 *
 * OFF IN PRODUCTION WHILE SMS IS PARKED. The till was phone-OTP only, so
 * parking SMS did not degrade the sign-in -- it removed the only way into the
 * till that existed. And the screen went on saying "A code was sent to
 * +974…" for a code that could never arrive, which is worse than an absent
 * route: the cashier stands there waiting, then retypes their number, then
 * decides the tablet is broken.
 *
 * A CONFIGURATION VALUE, NOT A DELETION, and the same seam the customer app
 * uses. SMS comes back when the Meta verification clears; it should come back
 * by flipping this, not by restoring code from a diff.
 *
 * Defaults OFF, so a new environment that nobody has thought about does not
 * quietly offer a route that cannot work. That is the same direction
 * `_production()` fails in and for the same reason.
 */
export const PHONE_SIGN_IN = import.meta.env.VITE_PHONE_SIGN_IN === "true";
